import { useMemo } from "react";
import { toast } from "sonner";
import {
    updatePilotageIndicator,
    useQueryPilotageIndicators,
} from "../api/queries";
import { EXCLUDED_INDICATOR_GROUPS } from "../constants";

const UNDO_DURATION = 5000;

const setIndicatorVisible = (code, visible) =>
    updatePilotageIndicator({ type: "indicator", code, visible });

const setGroupVisible = (code, visible) =>
    updatePilotageIndicator({ type: "group", code, visible });

/**
 * Préférences "Personnaliser l'affichage" de l'utilisateur connecté.
 * Un indicateur absent de la réponse (ou tant que le GET n'a pas répondu) est visible.
 */
const useIndicatorsVisibility = () => {
    const { data, isLoading } = useQueryPilotageIndicators();

    const groups = useMemo(
        () =>
            (data?.groups || []).filter(
                (g) => !EXCLUDED_INDICATOR_GROUPS.includes(g.code)
            ),
        [data]
    );

    const hiddenCodes = useMemo(
        () =>
            new Set(
                groups.flatMap((g) =>
                    g.indicators.filter((i) => !i.visible).map((i) => i.code)
                )
            ),
        [groups]
    );

    /** true si au moins un des codes est visible */
    const isVisible = (...codes) => codes.some((c) => !hiddenCodes.has(c));

    const getGroup = (groupCode) => groups.find((g) => g.code === groupCode);

    const hiddenCountByGroup = (groupCode) =>
        getGroup(groupCode)?.indicators.filter((i) => !i.visible).length ?? 0;

    const isGroupAllHidden = (groupCode) => {
        const group = getGroup(groupCode);
        return (
            !!group?.indicators.length &&
            group.indicators.every((i) => !i.visible)
        );
    };

    const hideWithUndo = (code, label) => {
        setIndicatorVisible(code, false);
        toast(`« ${label} » masqué`, {
            duration: UNDO_DURATION,
            action: {
                label: "Annuler",
                onClick: () => setIndicatorVisible(code, true),
            },
        });
    };

    /* Toast affiché uniquement une fois toutes les requêtes terminées avec succès */
    const showAll = async () => {
        const previous = [...hiddenCodes];
        if (!previous.length) return;
        const results = await Promise.all(
            groups
                .filter((g) => g.indicators.some((i) => !i.visible))
                .map((g) => setGroupVisible(g.code, true))
        );
        if (!results.every(Boolean)) return;
        toast("Tous les indicateurs sont réaffichés", {
            duration: UNDO_DURATION,
            action: {
                label: "Annuler",
                onClick: () =>
                    previous.forEach((code) =>
                        setIndicatorVisible(code, false)
                    ),
            },
        });
    };

    return {
        groups,
        isLoading,
        isVisible,
        hiddenCount: hiddenCodes.size,
        hiddenCountByGroup,
        isGroupAllHidden,
        setIndicatorVisible,
        setGroupVisible,
        hideWithUndo,
        showAll,
    };
};

export default useIndicatorsVisibility;
